import { Injectable } from '@angular/core';
import { fromEvent, Observable } from 'rxjs';
import { map, startWith, distinctUntilChanged } from 'rxjs/operators';

type Breakpoints = {
  [key in Breakpoint]: {
    min: number;
    max: number;
  }
};

type Breakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

@Injectable({
  providedIn: 'root'
})
export class BreakpointService {
  constructor() {
    this.event$ = fromEvent(window, 'resize');
  }
  
  private breakpoints: Breakpoints = {
    xs: {
      min: 0,
      max: 575
    },
    sm: {
      min: 576,
      max: 767
    },
    md: {
      min: 768,
      max: 991
    },
    lg: {
      min: 992,
      max: 1199
    },
    xl: {
      min: 1200,
      max: Infinity
    }
  };

  private event$: Observable<Event>;

  /**
   * Comparing to breakpoint
   * 
   * @param breakpoint<Breakpoint>
   * 
   * @return Observable<boolean
   */
  public is(breakpoint: Breakpoint): Observable<boolean> {
    return this.event$.pipe(
      map(event => this._is((event.target as Window).innerWidth, breakpoint)),
      startWith(this._is(window.innerWidth, breakpoint)),
      distinctUntilChanged()
    );
  }

  private _is(width: number, breakpoint: Breakpoint): boolean {
    return width >= this.breakpoints[breakpoint].min &&
      width <= this.breakpoints[breakpoint].max
      ? true
      : false;
  }

  /**
   * Is current breakpoint is less than given breakpoint
   * 
   * @param breakpoint<Breakpoint>
   * 
   * @return Observable<boolean>
   */
  public isLessThan(breakpoint: Breakpoint): Observable<boolean> {
    return this.event$.pipe(
      map(event =>
        this._isLessThan((event.target as Window).innerWidth, breakpoint)
      ),
      startWith(this._isLessThan(window.innerWidth, breakpoint)),
      distinctUntilChanged()
    );
  }

  private _isLessThan(width: number, breakpoint: Breakpoint): boolean {
    return width < this.breakpoints[breakpoint].min ? true : false;
  }

  /**
   * Is current breakpoint is less than given breakpoint
   * 
   * @param breakpoint<Breakpoint>
   * 
   * @return Observable<boolean>
   */
  public isMoreThan(breakpoint: Breakpoint): Observable<boolean> {
    return this.event$.pipe(
      map(event =>
        this._isMoreThan((event.target as Window).innerWidth, breakpoint)
      ),
      startWith(this._isMoreThan(window.innerWidth, breakpoint)),
      distinctUntilChanged()
    );
  }

  private _isMoreThan(width: number, breakpoint: Breakpoint): boolean {
    return width > this.breakpoints[breakpoint].max ? true : false;
  }

  /**
   * Current breakpoint observable
   * 
   * @return Breakpoint | null
   */
  public get now$(): Observable<Breakpoint | null> {
    return this.event$.pipe(
      map(event => this._now((event.target as Window).innerWidth)),
      startWith(this._now(window.innerWidth)),
      distinctUntilChanged()
    );
  }

  private _now(width: number): Breakpoint | null {
    let breakpoint = Object.keys(this.breakpoints)
      .map(key => {
        return {
          key: key as Breakpoint,
          min: this.breakpoints[key].min,
          max: this.breakpoints[key].max
        };
      })
      .find(el => width >= el.min && width <= el.max);
    return breakpoint ? breakpoint.key : null;
  }

  /**
   * Current breakpoint name
   * 
   * @return Breakpoint | null
   */
  public get now(): Breakpoint | null {
    return this._now(window.innerWidth);
  }

  /**
   * Is current breakpoint is less than given breakpoint
   * 
   * @param breakpoint<Breakpoint>
   * 
   * @return boolean
   */
  public nowLessThan(breakpoint: Breakpoint): boolean {
    return this._isLessThan(window.innerWidth, breakpoint);
  }

  /**
   * Is current breakpoint is more than given breakpoint
   * 
   * @param breakpoint<Breakpoint>
   * 
   * @return boolean
   */
  public nowMoreThan(breakpoint: Breakpoint): boolean {
    return this._isMoreThan(window.innerWidth, breakpoint);
  }
}
